/**
 * Form Templates Page.
 *
 * Lists all form templates and provides a builder for creating new ones.
 * Supports both generic forms and inspection checklists with sections,
 * pass/fail fields, and category filtering.
 *
 * Forms are always created in relation to assets. They are completed
 * through work orders, which provide the task/workflow context.
 */

import { useState } from "react";
import { FileText, Plus, Trash2, ClipboardCheck } from "lucide-react";
import { useFormTemplates, useCreateFormTemplate } from "@/features/forms/api";
import { toast } from "@/stores/toastStore";
import { FORM_FIELD_TYPES, INSPECTION_SECTIONS } from "@/types/form";
import type { FormFieldDefinition } from "@/types/form";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

function FormTemplatesPage() {
  const { data: templates, isLoading } = useFormTemplates();
  const createMutation = useCreateFormTemplate();

  const [showBuilder, setShowBuilder] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [templateType, setTemplateType] = useState<"form" | "inspection">(
    "inspection",
  );
  const [categoryFilter, setCategoryFilter] = useState("");
  const [fields, setFields] = useState<FormFieldDefinition[]>([]);

  // New field state.
  const [fLabel, setFLabel] = useState("");
  const [fType, setFType] = useState("text");
  const [fRequired, setFRequired] = useState(false);
  const [fOptions, setFOptions] = useState("");
  const [fSection, setFSection] = useState("");
  const [customSection, setCustomSection] = useState("");

  const addField = () => {
    if (!fLabel) return;
    const fieldName = fLabel
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
    const section = fSection === "__custom__" ? customSection : fSection;
    const field: FormFieldDefinition = {
      name: fieldName,
      label: fLabel,
      type: fType as FormFieldDefinition["type"],
      required: fRequired,
      options:
        (fType === "select" || fType === "pass_fail") && fOptions
          ? fOptions.split(",").map((o) => o.trim())
          : fType === "pass_fail"
            ? ["Pass", "Attention", "Fail"]
            : undefined,
      section: section || undefined,
      rating_type: fType === "pass_fail" ? "pass_fail" : undefined,
    };
    setFields([...fields, field]);
    setFLabel("");
    setFType("text");
    setFRequired(false);
    setFOptions("");
    setCustomSection("");
  };

  const removeField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (fields.length === 0) return;
    const cats = categoryFilter.trim()
      ? categoryFilter
          .split(",")
          .map((c) => c.trim().toLowerCase())
          .filter(Boolean)
      : undefined;
    try {
      await createMutation.mutateAsync({
        name,
        description: description || undefined,
        template_type: templateType,
        category_filter: cats,
        schema: fields,
      });
      setShowBuilder(false);
      setName("");
      setDescription("");
      setFields([]);
      setCategoryFilter("");
      setTemplateType("inspection");
      toast.success("Form template created");
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error(
        "Failed to create form template",
        msg ||
          "Please check the form and try again. Ensure database migrations have been run.",
      );
    }
  };

  // Group fields by section for the preview.
  const sectionGroups = (() => {
    const groups = new Map<string, FormFieldDefinition[]>();
    for (const f of fields) {
      const key = f.section || "(No section)";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(f);
    }
    return Array.from(groups.entries());
  })();

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Form Templates
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Create custom inspection checklists and intake forms for asset
            inspections.
          </p>
        </div>
        <Button onClick={() => setShowBuilder(!showBuilder)}>
          {showBuilder ? (
            "Cancel"
          ) : (
            <>
              <Plus className="h-4 w-4" /> New Template
            </>
          )}
        </Button>
      </div>

      {/* Builder */}
      {showBuilder && (
        <Card>
          <form onSubmit={handleCreate} className="space-y-5">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">
              Build New Form Template
            </h3>

            {/* Template info */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Template Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="e.g., Tractor Intake Inspection"
              />
              <Input
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="When and how this form is used"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                  Template Type
                </label>
                <div className="mt-1.5 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setTemplateType("inspection")}
                    className={`flex-1 flex items-center justify-center gap-2 h-[38px] rounded-[var(--radius-md)] border text-sm font-semibold transition-all ${
                      templateType === "inspection"
                        ? "border-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/8 text-[var(--color-interactive-primary)]"
                        : "border-[var(--border-default)] text-[var(--text-tertiary)] hover:border-[var(--border-strong)]"
                    }`}
                  >
                    <ClipboardCheck className="h-4 w-4" /> Inspection
                  </button>
                  <button
                    type="button"
                    onClick={() => setTemplateType("form")}
                    className={`flex-1 flex items-center justify-center gap-2 h-[38px] rounded-[var(--radius-md)] border text-sm font-semibold transition-all ${
                      templateType === "form"
                        ? "border-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/8 text-[var(--color-interactive-primary)]"
                        : "border-[var(--border-default)] text-[var(--text-tertiary)] hover:border-[var(--border-strong)]"
                    }`}
                  >
                    <FileText className="h-4 w-4" /> General Form
                  </button>
                </div>
              </div>
              <Input
                label="Asset Categories (optional, comma-separated)"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                placeholder="e.g., tractor, equipment (blank = all categories)"
              />
            </div>

            {/* Add Field */}
            <div className="rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-4">
              <h4 className="mb-3 text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.1em]">
                Add Field
              </h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
                <div className="sm:col-span-4">
                  <Input
                    label="Label"
                    value={fLabel}
                    onChange={(e) => setFLabel(e.target.value)}
                    placeholder="e.g., Engine Start Behavior"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                    Type
                  </label>
                  <select
                    value={fType}
                    onChange={(e) => setFType(e.target.value)}
                    className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
                  >
                    {FORM_FIELD_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                {templateType === "inspection" && (
                  <div className="sm:col-span-3">
                    <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                      Section
                    </label>
                    <select
                      value={fSection}
                      onChange={(e) => setFSection(e.target.value)}
                      className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
                    >
                      <option value="">No section</option>
                      {INSPECTION_SECTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                      <option value="__custom__">Custom section...</option>
                    </select>
                    {fSection === "__custom__" && (
                      <input
                        value={customSection}
                        onChange={(e) => setCustomSection(e.target.value)}
                        placeholder="Section name"
                        className="mt-1.5 block w-full h-[34px] rounded-[var(--radius-sm)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)]"
                      />
                    )}
                  </div>
                )}
                <div
                  className={`flex items-end gap-3 ${templateType === "inspection" ? "sm:col-span-3" : "sm:col-span-6"}`}
                >
                  <label className="flex items-center gap-2 text-sm text-[var(--text-primary)] pb-2">
                    <input
                      type="checkbox"
                      checked={fRequired}
                      onChange={(e) => setFRequired(e.target.checked)}
                      className="h-4 w-4 rounded border-[var(--border-default)]"
                    />
                    Required
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={addField}
                    disabled={!fLabel}
                  >
                    Add
                  </Button>
                </div>
              </div>
              {fType === "select" && (
                <div className="mt-2">
                  <Input
                    label="Options (comma-separated)"
                    value={fOptions}
                    onChange={(e) => setFOptions(e.target.value)}
                    placeholder="Good, Fair, Poor"
                  />
                </div>
              )}
            </div>

            {/* Fields Preview — grouped by section */}
            {fields.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.1em]">
                  Fields ({fields.length})
                </h4>
                {sectionGroups.map(([section, sectionFields]) => (
                  <div key={section}>
                    {section !== "(No section)" && (
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
                          {section}
                        </span>
                        <div className="flex-1 h-px bg-[var(--border-subtle)]" />
                      </div>
                    )}
                    <div className="space-y-1">
                      {sectionFields.map((f) => {
                        const globalIdx = fields.indexOf(f);
                        return (
                          <div
                            key={globalIdx}
                            className="flex items-center justify-between rounded-[var(--radius-md)] bg-[var(--surface-base)] border-[0.5px] border-[var(--border-subtle)] px-3 py-2"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-[var(--text-primary)]">
                                {f.label}
                              </span>
                              <Badge
                                variant={
                                  f.type === "pass_fail" ? "warning" : "default"
                                }
                              >
                                {f.type === "pass_fail" ? "Pass/Fail" : f.type}
                              </Badge>
                              {f.required && (
                                <Badge variant="danger">required</Badge>
                              )}
                              {f.options && f.type !== "pass_fail" && (
                                <span className="text-xs text-[var(--text-tertiary)]">
                                  [{f.options.join(", ")}]
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeField(globalIdx)}
                              className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)] transition-colors"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end">
              <Button
                type="submit"
                isLoading={createMutation.isPending}
                disabled={fields.length === 0}
              >
                Create Template
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Templates List */}
      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : templates && templates.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <Card key={t.id} variant="interactive">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  {t.template_type === "inspection" ? (
                    <ClipboardCheck className="h-5 w-5 text-[var(--color-interactive-primary)]" />
                  ) : (
                    <FileText className="h-5 w-5 text-[var(--text-tertiary)]" />
                  )}
                  <div>
                    <h3 className="font-semibold text-[var(--text-primary)]">
                      {t.name}
                    </h3>
                    <p className="text-[10px] text-[var(--text-tertiary)]">
                      v{t.version}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {t.template_type === "inspection" && (
                    <Badge variant="info">Inspection</Badge>
                  )}
                  <Badge variant={t.is_active ? "success" : "default"}>
                    {t.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>
              {t.description && (
                <p className="mt-2 text-xs text-[var(--text-secondary)]">
                  {t.description}
                </p>
              )}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-[var(--text-tertiary)]">
                  {t.schema.length} field{t.schema.length !== 1 ? "s" : ""}
                </span>
                {t.category_filter && t.category_filter.length > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--surface-sunken)] text-[var(--text-tertiary)]">
                    {t.category_filter.join(", ")}
                  </span>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <ClipboardCheck />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No form templates
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Create an inspection template to get started with asset
              inspections.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

export default FormTemplatesPage;
